// AI bridge — calls the existing Supabase Edge Function `ai-proxy`
// directly from the browser. Mirrors the contract callers (the
// renderer) expect from window.screenplay.aiChat: returns
// `{ ok: true, text, blocks }` or `{ ok: false, error, code }`.
//
// Differences from src/main/ai.js:
//   - The JWT lives in supabase-js's session; we fetch it via
//     `supabase.auth.getSession()` instead of the Electron auth module.
//   - The `read_branch` tool reads the latest version on a named draft
//     from `web_script_versions` (Postgres), not a git checkout.

import { RENDER_CHART_TOOL, READ_BRANCH_TOOL } from './ai-constants.js';

const REQUEST_TIMEOUT_MS = 60_000;
const MAX_RETRIES = 2;
const BACKOFF_BASE_MS = 250;
const MAX_TOOL_ITERATIONS = 2;
const MAX_HISTORY_TURNS = 20;
const RETRYABLE_CODES = new Set(['rate-limit', 'service', 'timeout', 'network']);

class AIError extends Error {
  constructor(message, code, status) {
    super(message);
    this.name = 'AIError';
    this.code = code || 'unknown';
    if (status != null) this.status = status;
  }
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function classifyHttpStatus(s) {
  if (s === 401 || s === 403) return 'auth';
  if (s === 413) return 'input-too-large';
  if (s === 429) return 'rate-limit';
  if (s >= 500) return 'service';
  if (s >= 400) return 'request';
  return 'unknown';
}

function humanize(code, message) {
  switch (code) {
    case 'tier-required':
      return 'AI is part of Pro. Upgrade to Pro to use Chat.';
    case 'quota-exceeded':
      return 'You have used this month’s AI allowance. Resets on the 1st.';
    case 'rate-limit':
      return "You're sending requests too quickly. Wait a moment and try again.";
    case 'input-too-large':
      return 'That request is too large. Shorten the script or selection and try again.';
    case 'auth':
    case 'unauthorized':
    case 'auth-missing':
      return 'Sign in to use AI (Settings → Account).';
    default:
      return message;
  }
}

function characterSummary(script) {
  if (!script || !script.characters) return '';
  return Object.values(script.characters)
    .sort((a, b) => b.lines.length - a.lines.length)
    .slice(0, 10)
    .map((c) => {
      const sample = c.lines
        .slice(0, 3)
        .map((l) => `  - "${l}"`)
        .join('\n');
      return `${c.name} (${c.lines.length} lines)\n${sample}`;
    })
    .join('\n\n');
}

function fullScriptText(script) {
  if (!script || !script.scenes) return '';
  const out = [];
  for (const scene of script.scenes) {
    out.push(`[[Scene ${scene.number}]]`);
    for (const b of scene.blocks) {
      if (!b || typeof b.text !== 'string') continue;
      switch (b.type) {
        case 'scene-heading':
          out.push(b.text);
          break;
        case 'character':
          out.push(`\n${b.text}`);
          break;
        case 'parenthetical': {
          const t = b.text.trim();
          out.push(t.startsWith('(') ? t : `(${t.replace(/^\(|\)$/g, '')})`);
          break;
        }
        case 'transition':
          out.push(`\n${b.text}`);
          break;
        default:
          out.push(b.text);
      }
    }
    out.push('');
  }
  return out.join('\n').trim();
}

function contentToBlocks(content) {
  const blocks = [];
  for (const c of content || []) {
    if (!c || typeof c !== 'object') continue;
    if (c.type === 'text' && typeof c.text === 'string' && c.text.length) {
      blocks.push({ kind: 'prose', text: c.text });
    } else if (c.type === 'tool_use' && c.name === 'render_chart' && c.input) {
      blocks.push({ kind: 'chart', spec: c.input });
    }
  }
  const text = blocks
    .map((b) => {
      if (b.kind === 'prose') return b.text;
      const t = b.spec && b.spec.title ? ` — "${b.spec.title}"` : '';
      const ty = b.spec && b.spec.type ? b.spec.type : 'chart';
      return `\n[chart: ${ty}${t}]\n`;
    })
    .join('')
    .trim();
  return { text, blocks };
}

async function postProxyOnce({ supabaseUrl, jwt, body }) {
  if (!supabaseUrl) throw new AIError('Supabase not configured.', 'config');
  const url = `${supabaseUrl.replace(/\/$/, '')}/functions/v1/ai-proxy`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${jwt}` },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timer);
    if (err && err.name === 'AbortError') {
      throw new AIError(
        `Request to Claude timed out after ${REQUEST_TIMEOUT_MS / 1000}s.`,
        'timeout',
      );
    }
    throw new AIError(err && err.message ? err.message : 'Network error', 'network');
  }
  clearTimeout(timer);
  let parsed = null;
  try {
    parsed = await res.json();
  } catch {
    /* empty */
  }
  if (!res.ok || (parsed && parsed.ok === false)) {
    const code = (parsed && parsed.code) || classifyHttpStatus(res.status);
    const message = (parsed && parsed.error) || (parsed && parsed.code) || `HTTP ${res.status}`;
    throw new AIError(humanize(code, message), code, res.status);
  }
  if (!parsed || !Array.isArray(parsed.content)) {
    throw new AIError('Could not parse response from AI service.', 'parse', res.status);
  }
  return { content: parsed.content, model: parsed.model, usage: parsed.usage };
}

async function postProxy(args) {
  let lastErr;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      return await postProxyOnce(args);
    } catch (err) {
      lastErr = err;
      const retryable = err instanceof AIError && RETRYABLE_CODES.has(err.code);
      if (!retryable || attempt === MAX_RETRIES) break;
      await sleep(BACKOFF_BASE_MS * Math.pow(2, attempt) + Math.floor(Math.random() * 100));
    }
  }
  throw lastErr;
}

// Read the latest version on the named draft for the current script.
// Replaces the Electron `read_branch` tool (which checked out a git
// branch) with a single Postgres SELECT.
async function readDraftContent({ supabase, scriptId, branch }) {
  if (!supabase || !scriptId) return { ok: false, error: 'no active script' };
  try {
    const { data: draft, error: dErr } = await supabase
      .from('web_drafts')
      .select('id')
      .eq('script_id', scriptId)
      .eq('name', branch)
      .maybeSingle();
    if (dErr) throw dErr;
    if (!draft) return { ok: false, error: `unknown draft: ${branch}` };
    const { data: ver, error: vErr } = await supabase
      .from('web_script_versions')
      .select('content')
      .eq('draft_id', draft.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (vErr) throw vErr;
    if (!ver) return { ok: false, error: 'draft has no saved versions' };
    return { ok: true, content: ver.content };
  } catch (err) {
    return { ok: false, error: err.message };
  }
}

async function listDraftsFor({ supabase, scriptId }) {
  if (!supabase || !scriptId) return [];
  const { data } = await supabase.from('web_drafts').select('name').eq('script_id', scriptId);
  return (data || []).map((d) => d.name);
}

export function buildAIHandlers({ supabase, env }) {
  return {
    async aiChat(messages, script, currentSceneIndex) {
      if (!supabase) {
        return { ok: false, error: 'Sign in to use AI.', code: 'auth-missing' };
      }
      const { data: sess } = await supabase.auth.getSession();
      const jwt = sess?.session?.access_token;
      if (!jwt) return { ok: false, error: 'Sign in to use AI.', code: 'auth-missing' };

      const scriptId = script && typeof script.filePath === 'string' ? script.filePath : null;
      const drafts = await listDraftsFor({ supabase, scriptId });
      const branchToolEnabled = !!scriptId;

      let scriptContext = null;
      if (script) {
        const cursorScene =
          typeof currentSceneIndex === 'number' && script.scenes && script.scenes[currentSceneIndex]
            ? script.scenes[currentSceneIndex]
            : null;
        scriptContext = {
          title: script.title,
          characterSummary: characterSummary(script),
          fullScript: fullScriptText(script),
          cursor: cursorScene
            ? { number: cursorScene.number, heading: cursorScene.heading || '' }
            : null,
        };
      }

      const safeMessages = (messages || [])
        .map((m) => ({
          role: m.role === 'assistant' ? 'assistant' : 'user',
          content: typeof m.content === 'string' ? m.content : String(m.content || ''),
        }))
        .filter((m) => m.content.trim().length);
      if (safeMessages.length === 0) {
        safeMessages.push({ role: 'user', content: 'Give me a brief read on this script.' });
      }
      const trimmed = safeMessages.slice(-MAX_HISTORY_TURNS * 2);

      try {
        const conversation = trimmed.slice();
        let finalContent = [];
        for (let iter = 0; iter < MAX_TOOL_ITERATIONS + 1; iter++) {
          const { content } = await postProxy({
            supabaseUrl: env.SUPABASE_URL,
            jwt,
            body: {
              feature: 'chat',
              payload: {
                messages: conversation,
                scriptContext,
                drafts: branchToolEnabled ? drafts : [],
                branchToolEnabled,
              },
              options: {},
            },
          });
          finalContent = content;
          const branchCalls = (content || []).filter(
            (c) => c && c.type === 'tool_use' && c.name === 'read_branch',
          );
          if (branchCalls.length === 0 || iter === MAX_TOOL_ITERATIONS) break;
          conversation.push({ role: 'assistant', content });
          const toolResults = [];
          for (const call of branchCalls) {
            const branch =
              call.input && typeof call.input.branch === 'string' ? call.input.branch : '';
            const r = await readDraftContent({ supabase, scriptId, branch });
            const resultText = r.ok
              ? `<DRAFT branch="${branch}">\n${r.content}\n</DRAFT>`
              : `Error: ${r.error}`;
            toolResults.push({ type: 'tool_result', tool_use_id: call.id, content: resultText });
          }
          conversation.push({ role: 'user', content: toolResults });
        }
        const out = contentToBlocks(finalContent);
        return { ok: true, text: out.text, blocks: out.blocks };
      } catch (err) {
        return {
          ok: false,
          error: err.message || String(err),
          code: err && err.code,
          status: err && err.status,
        };
      }
    },

    // Outline scaffold from a one-line logline. Mirrors
    // src/main/ai.js's scaffold path — same Edge Function feature
    // ('scaffold'), same response envelope.
    async aiScaffold(logline) {
      if (!supabase) return { ok: false, error: 'Sign in to use AI.', code: 'auth-missing' };
      const { data: sess } = await supabase.auth.getSession();
      const jwt = sess?.session?.access_token;
      if (!jwt) return { ok: false, error: 'Sign in to use AI.', code: 'auth-missing' };
      const safeLogline = typeof logline === 'string' ? logline.trim() : '';
      if (!safeLogline) return { ok: false, error: 'Logline required', code: 'bad-request' };
      try {
        const { content } = await postProxy({
          supabaseUrl: env.SUPABASE_URL,
          jwt,
          body: {
            feature: 'scaffold',
            payload: { logline: safeLogline },
            options: {},
          },
        });
        const out = contentToBlocks(content);
        return { ok: true, text: out.text, blocks: out.blocks };
      } catch (err) {
        return {
          ok: false,
          error: err.message || String(err),
          code: err && err.code,
          status: err && err.status,
        };
      }
    },
  };
}

// Re-export the tool constants so unit tests can compare web ↔ main.
export { RENDER_CHART_TOOL, READ_BRANCH_TOOL };
